import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobs } from 'src/hooks/useJobs';

import { Alert } from 'src/design/atoms/Alert';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Input } from 'src/design/atoms/Input';
import { MRT_ColumnDef } from 'mantine-react-table';
import { TimesheetDto } from 'src/api/FiretrolApiClient';
import {
  formatWorkWeekId,
  getCurrentWorkWeek,
  parseWorkWeekId,
} from 'src/utils/workWeek';
import { formatHours, resolveWorkflowButtonColor } from 'src/utils';
import { useTimesheetsForJob } from 'src/api/queries/timesheets/useTimesheetsForJob';
import { useAuth } from 'src/hooks/useAuth';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { PageBreadcrumbs } from 'src/design/molecules/PageBreadcrumbs';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  WeekSelector,
  currentWeekFilter,
  tempOptions,
} from './components/WeekSelector';
import { IDateOption } from './types';
import { IRosterMember, withTimesheetUsers } from './timesheetRoster';

const EMPTY_TIMESHEETS: TimesheetDto[] = [];

interface ITeamTimesheet extends IRosterMember {
  timesheet: TimesheetDto | null;
}

const useTimesheetsColumns = (
  selectedWeek: IDateOption,
  selectedJobId: number,
): MRT_ColumnDef<ITeamTimesheet>[] => {
  const { year, weekNumber } = parseWorkWeekId(selectedWeek.id);
  return [
    {
      accessorKey: 'user.fullName',
      header: 'Employee Name',
      minSize: 300,
      Cell: ({ cell }) => {
        return (
          <LinkButton
            to={`/jobs/${selectedJobId}/timesheets/user/${cell.row.original.userId}/week/${year}/${weekNumber}`}
          >
            {cell.row.original.user?.fullName || ''}
          </LinkButton>
        );
      },
    },

    {
      id: 'status',
      header: 'Status',
      filterVariant: 'multi-select',
      enableColumnFilterModes: false,
      accessorFn: (row) => {
        return row.timesheet?.status || '--';
      },
      Cell: ({ cell }) => {
        const timesheet = cell.row.original.timesheet;
        const status = timesheet?.status;

        if (!status) return <span>--</span>;

        if (timesheet.stepIndex === -1) {
          return <Badge color="gray">{timesheet.status.toUpperCase()}</Badge>;
        }
        if (timesheet.status === 'Rejected') {
          return <Badge color="red">{timesheet.status.toUpperCase()}</Badge>;
        }
        const step = timesheet.workflow?.steps[timesheet.stepIndex];
        const color = resolveWorkflowButtonColor(step?.buttonColor) || 'blue';
        return <Badge color={color}>{timesheet.status.toUpperCase()}</Badge>;
      },
    },
    {
      id: 'hours',
      header: 'Total Hours',
      filterVariant: 'range',
      filterFn: 'between',
      minSize: 300,
      accessorFn: (row) => {
        const total =
          row.timesheet?.rows?.reduce((acc, r) => acc + Number(r.total), 0) ||
          0;
        return formatHours(total);
      },
    },
    {
      id: 'created_by_id',
      header: 'Created By',
      minSize: 300,
      accessorFn: (row) => {
        return row.timesheet?.createdBy?.fullName || '--';
      },
    },
    {
      id: 'supervisor',
      header: 'Supervisor',
      minSize: 300,
      accessorFn: (row) => {
        return row.timesheet?.user?.supervisor?.fullName || '--';
      },
    },
  ];
};

export const Timesheets: React.FC = () => {
  const { selectedJob, team, loadingTeam } = useJobs();
  const navigate = useNavigate();
  const { user, scopes } = useAuth();

  const isTimesheetAdmin = React.useMemo(
    () =>
      scopes.includes('sys_admin') || scopes.includes('jobs_timesheets_admin'),
    [scopes],
  );

  // Admin-only toggle - non-admins are always filtered server-side
  const [showOnlyMyTeam, setShowOnlyMyTeam] = React.useState(false);

  const [selectedWeek, setSelectedWeek] = React.useState<IDateOption>(
    tempOptions.find(
      (o) =>
        o.id ===
        formatWorkWeekId(
          getCurrentWorkWeek().year,
          getCurrentWorkWeek().weekNumber,
        ),
    ) || currentWeekFilter,
  );

  // Filter team list to match what the user can see
  // Non-admins: always filtered | Admins: filtered only when toggle is on
  const filterByHierarchy = !isTimesheetAdmin || showOnlyMyTeam;

  const { year: selectedYear, weekNumber: selectedWeekNumber } =
    parseWorkWeekId(selectedWeek.id);

  const {
    data: timesheets = EMPTY_TIMESHEETS,
    isLoading: loading,
    isError,
    // Only admins need to pass supervisorId - non-admins are enforced server-side
  } = useTimesheetsForJob(selectedJob?.id, {
    weekId: selectedWeekNumber,
    year: selectedYear,
    supervisorId: showOnlyMyTeam ? user?.id : undefined,
  });

  const teamTimesheets: ITeamTimesheet[] = React.useMemo(() => {
    const currentTimesheets = timesheets.filter(
      (v) => v.weekId === selectedWeekNumber && v.year === selectedYear,
    );
    return withTimesheetUsers(team, currentTimesheets)
      .filter((teamMember) => {
        if (filterByHierarchy) {
          return (
            teamMember.userId === user?.id ||
            teamMember.user?.supervisorId === user?.id ||
            teamMember.user?.supervisor?.supervisorId === user?.id
          );
        }
        return true;
      })
      .map((teamMember) => {
        const userTimesheet = currentTimesheets.find(
          (timesheet) => timesheet.userId === teamMember.userId,
        );
        return {
          userId: teamMember.userId,
          user: teamMember.user,
          timesheet: userTimesheet || null,
        };
      });
  }, [
    team,
    timesheets,
    selectedWeekNumber,
    selectedYear,
    filterByHierarchy,
    user?.id,
  ]);

  // Steps to the adjacent option in the chronologically-sorted week list, rolling across years.
  const stepWeek = (direction: 1 | -1) => {
    const currentIndex = tempOptions.findIndex((o) => o.id === selectedWeek.id);
    const nextIndex = currentIndex + direction;
    const val = tempOptions[nextIndex];
    if (val) {
      setSelectedWeek(val);
    }
  };
  const nextWeek = () => stepWeek(1);
  const prevWeek = () => stepWeek(-1);

  const selectedJobId = selectedJob?.id || 0;
  const columns = useTimesheetsColumns(selectedWeek, selectedJobId);

  const tableState = useTableState({
    identifier: `job-timesheets`,
    columns: columns,
    defaultSorting: [{ id: 'user.fullName', desc: false }],
  });

  const tableExports = useTableExports<ITeamTimesheet>(
    `timesheets-week-${selectedWeek.id}`,
    (data) => {
      return data.map((item) => ({
        ['Job Name']: selectedJob?.name,
        ['Job Number']: selectedJob?.jobNumber,
        ['Period']: selectedWeek?.label,
        ['Employee Name']: item.user?.fullName || '--',
        ['Status']: item.timesheet?.status || '--',
        ['Total Hours']:
          item.timesheet?.rows?.reduce((acc, r) => acc + Number(r.total), 0) ||
          0,
        ['Created By']: item.timesheet?.createdBy?.fullName || '--',
        ['Supervisor']: item.timesheet?.user?.supervisor?.fullName || '--',
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns: columns,
      data: teamTimesheets,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          {selectedWeek && team[0] && (
            <CreateButton
              onClick={() =>
                navigate(
                  `/jobs/${selectedJobId}/timesheets/user/${team[0]?.userId}/week/${selectedYear}/${selectedWeekNumber}`,
                )
              }
            >
              Create Timesheet
            </CreateButton>
          )}
        </Group>
      ),
    },
    getRowId: (row) => String(row.userId),
    totalCount: team.length,
    isLoading: loading,
    isFetching: false,
    isError,
    ...tableExports,
  });

  return (
    <Stack>
      <PageBreadcrumbs
        items={[
          { text: 'Jobs', href: '/jobs' },
          {
            text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
            href: `/jobs/${selectedJob?.id}/summary`,
          },
          { text: 'Timesheets' },
        ]}
      />
      {!selectedJob?.timesheetWorkflow && (
        <Alert color="red">
          Default workflow does not exist. Please contact an administrator.
        </Alert>
      )}
      <Group align="end">
        <Input.Wrapper label="Work Week" w="400px">
          <WeekSelector
            hideLabel
            value={selectedWeek}
            onChange={(v) => setSelectedWeek(v)}
          />
        </Input.Wrapper>
        <Group gap="sm" align="end">
          <BasicButton onClick={prevWeek}>Prev</BasicButton>
          <BasicButton onClick={nextWeek}>Next</BasicButton>
        </Group>
        {isTimesheetAdmin && (
          <>
            <Checkbox
              label="Show Only My Team"
              description="Only show timesheets for users that are supervised by you"
              checked={showOnlyMyTeam}
              onChange={(e) => setShowOnlyMyTeam(e.currentTarget.checked)}
            />
          </>
        )}
      </Group>
      {table}
    </Stack>
  );
};
